/**
 * The camp (M3-06) — fire, food and rest, and the rules that read world state
 * to run them.
 *
 * `needs.ts` holds the meters and what each band of them is worth; this holds
 * everything that has to look at a tile, a satchel, a carcass or a fire. The
 * same split `mind.ts`/`beasts.ts` and `weather.ts`/`sky.ts` make, and forced
 * by the same constraint: `world.ts` names `Needs`, so `needs.ts` may not
 * import world state.
 *
 * The survival micro-loop this closes, end to end: **kill something, cut it up,
 * lay a fire, cook it, eat, and lie down beside it until you are whole.** Every
 * one of those is a verb here, every one of them can fail for a reason a player
 * can see, and none of them can kill you.
 *
 * Three design commitments worth stating, because each is easy to undo:
 *
 * - **Nothing here is a recipe tree.** One `cook` verb that turns raw into
 *   cooked, and that is the whole of crafting in the MVP. Depth is post-MVP,
 *   and a two-ingredient recipe system built now would be a system built
 *   against no gameplay.
 * - **Rest is a place, not a resource.** `restQualityAt` asks the ground and
 *   the fire; there is no rested meter to grind. That is what makes a camp a
 *   decision about *where you are* rather than a bar to top up.
 * - **The fire can get away from you.** Built on dry grass and left, it
 *   spreads — and the ground it has burned cannot burn again, so it is a
 *   frightening mistake rather than an unbounded one.
 */
import { capabilityFault } from './affordance.js';
import { chebyshev, DIRECTION_VECTORS, DIRECTIONS } from './actor.js';
import { parseBuild, parseButcher, parseCook, parseDrop, parseEat, parseEquip, parseFeed, parsePrepare, parseRest, } from './actions.js';
import { carcassStage } from './ecology.js';
import { isChannelling, isCommitted, isHelpless, kitOf, maxStamina, STAMINA_REGEN, STAMINA_SCALE, } from './fighter.js';
import { createLight, feedFire, FIRE_FEED_FLOOR, FIRE_MAX_TICKS, FIRE_RADIUS, FUEL_TAG, isBurning, } from './light.js';
import { effectOf, isRemedy, relieves } from './herbs.js';
import { countOf, createInventory, createLore, learn, learnRemedy, nameFor, removeItem, stackIndexForKey, addItem, } from './item.js';
import { isPassable, isSheltered, tileAt, tileDefAt, setTile } from './map.js';
import { hasLineOfSight } from './senses.js';
import { createNeeds, expireRelief, hungerBand, MAX_FOOD, isIll, isRelieved, isResting, isWellRested, nourish, REST_RECOVERY, REST_SPEEDUP, SICKNESS_FEVER, SICKNESS_TICKS, starve, takeRelief, WELL_RESTED_REGEN, WELL_RESTED_TICKS, } from './needs.js';
import { hungerRate } from './marks.js';
import { advanceRecovery } from './recovery.js';
import { gameTime, TICKS_PER_GAME_HOUR, TICKS_PER_SECOND } from './time.js';
import { recomputeFever } from './infection.js';
import { advanceDailyRecovery, BLOOD_REGEN_PER_DAY, BLOOD_SCALE, MAX_BLOOD_MILLI, nourishmentFor, } from './vitals.js';
import { flammability, isRaining, KINDLE_FLOOR, kindleChance, wetnessOf } from './weather.js';
/* -------------------------------------------------------------------------- *
 * Timings and tags
 * -------------------------------------------------------------------------- */
/** Laying a fire properly: fifteen seconds of kneeling in the open. */
export const BUILD_TICKS = TICKS_PER_SECOND * 15;
/** Cutting up a carcass. The longest thing in the camp, and it should be. */
export const BUTCHER_TICKS = TICKS_PER_SECOND * 20;
/** Cooking. Long enough that you do it *at* a camp rather than in passing. */
export const COOK_TICKS = TICKS_PER_SECOND * 12;
/**
 * Making up a poultice or a brew (M4-03). The longest camp job but butchery,
 * and it should be: this is the evening's work you do *before* the trouble, and
 * the thing you are buying is two days of being better prepared than you were.
 */
export const PREPARE_TICKS = TICKS_PER_SECOND * 18;
/** Content tags this module asks about. Tags rather than types — see `ItemDef`. */
export const FOOD_TAG = 'food';
export const RAW_TAG = 'raw';
export const ROASTABLE_TAG = 'roastable';
export const COOKED_TAG = 'cooked';
/**
 * Struck steel and a stone. Carried, never spent, and it halves how often the
 * tinder refuses — which is the ticket's "flint item halves failure", and the
 * reason it is worth the 40 grams in a wet week.
 */
export const FLINT_TAG = 'flint';
/**
 * A prepared bundle of dry tinder. Unlike loose fuel, it has been kept inside
 * a fold of cloth; under a roof it makes the first flame dependable even after
 * a wet march.
 */
export const TINDER_TAG = 'tinder';
/** How much of the remaining chance of failure a flint takes away. */
export const FLINT_FACTOR = 0.5;
/** Chance a raw meal turns on you. A gamble worth taking when the alternative is nothing. */
export const RAW_SICKNESS_CHANCE = 0.35;
/**
 * A fire within reach — one built here, or the village hearth.
 *
 * The hearth counts, and is not a `LightSource`: it is a tile, it has been
 * burning since before anybody in the valley was born, and giving it a record
 * with a fuel deadline would mean somebody had to remember to feed the village.
 * Anything that asks "is there a fire here" asks this, so the answer is the same
 * for cooking, for resting and for warmth.
 */
export function fireAt(world, x, y, tick) {
    for (const [dx, dy] of [[0, 0], ...DIRECTIONS.map((d) => DIRECTION_VECTORS[d])]) {
        if (tileAt(world.map, x + dx, y + dy) === 'hearth')
            return 'hearth';
    }
    for (const light of Object.values(world.lights)) {
        if (light.kind !== 'fire')
            continue;
        if (!isBurning(light, tick))
            continue;
        if (chebyshev(light.x, light.y, x, y) > 1)
            continue;
        if (hasLineOfSight(world.map, x, y, light.x, light.y))
            return 'fire';
    }
    return null;
}
/** The fire nearest a tile that could actually be fed, or `null`. */
function feedableFire(world, x, y, tick) {
    let best = null;
    let bestDistance = Infinity;
    // Sorted by id so two fires at the same distance resolve the same way on
    // every machine and in every replay.
    for (const id of Object.keys(world.lights).sort()) {
        const light = world.lights[id];
        if (!light || light.kind !== 'fire' || !isBurning(light, tick))
            continue;
        const distance = chebyshev(light.x, light.y, x, y);
        if (distance > 1 || distance >= bestDistance)
            continue;
        best = light;
        bestDistance = distance;
    }
    return best;
}
/**
 * Will a fire laid on this tile get into the ground?
 *
 * Asked of the *material* rather than of a list of tile kinds, which is the
 * whole reason `Material` exists: heather, bracken and dry peat burn, slate and
 * a beaten floor do not, and the rule should not have to be updated every time
 * M3 adds a biome.
 */
export const TINDER_MATERIALS = ['plant', 'peat'];
export function isTinder(world, x, y) {
    return TINDER_MATERIALS.includes(tileDefAt(world.map, x, y).material);
}
/**
 * Where you are sleeping, as one of four words.
 *
 * Ordered best-first in the checks, because the tiers are a ladder and a tile
 * that qualifies for `bed` also qualifies for `dry`. The village is `bed`
 * because there is a roof, a floor and a fire that never goes out — which is
 * the concentration of players in the village the ticket is after, bought with
 * a mechanic rather than with a rule.
 */
export function restQualityAt(world, x, y, tick, sleeperId) {
    const tile = tileAt(world.map, x, y);
    const fire = fireAt(world, x, y, tick);
    // The Meddyg's cot (M4-08): the best rest in the valley, and there is one of
    // it. Occupancy is checked here rather than by a rule elsewhere because "is
    // somebody already in the bed" is a question about *this* place — and the
    // honest answer for the second patient is the hall floor beside it, which is
    // what the `bed`/`fire` tiers below already describe.
    if (tile === 'cot') {
        const taken = Object.values(world.actors ?? {}).some((other) => other.x === x && other.y === y && other.id !== sleeperId);
        if (!taken)
            return 'cot';
    }
    if (fire === 'hearth' && (tile === 'floor' || tile === 'door' || tile === 'cot'))
        return 'bed';
    if (fire !== null)
        return 'fire';
    // Under the weather and on ground that holds water is the worst of it. Bog,
    // ford, saltmarsh and shore all sit on materials that are wet by their nature
    // whatever the sky is doing.
    const material = tileDefAt(world.map, x, y).material;
    if (material === 'water' || material === 'peat')
        return 'exposed';
    // A roof changes the whole question (RC1-07): the rain is the reason a fireless
    // camp drops to `exposed`, and under a stick hut the rain does not reach you.
    // So a sheltered tile is never worse than `dry`, which is what makes a bothy a
    // place worth crossing the moor to in a downpour rather than four idle walls.
    if (isSheltered(world.map, x, y))
        return 'dry';
    if (isRaining(world.weather.kind) || wetnessOf(world.weather) > 0.6)
        return 'exposed';
    return 'dry';
}
/* -------------------------------------------------------------------------- *
 * Satchel helpers
 * -------------------------------------------------------------------------- */
/** The first thing in this satchel carrying a tag, or `null`. */
export function taggedItem(world, catalogue, actorId, tag) {
    const inventory = world.inventories[actorId];
    if (!inventory)
        return null;
    for (const stack of inventory.items) {
        const def = catalogue.item?.(stack.defId);
        if (def?.tags.includes(tag))
            return def;
    }
    return null;
}
export function hasTag(world, catalogue, actorId, tag) {
    return taggedItem(world, catalogue, actorId, tag) !== null;
}
/** The needs record for an actor, created on first use like `lore` and `inventories`. */
export function needsOf(world, actorId) {
    return (world.needs[actorId] ??= createNeeds());
}
/**
 * The one gate every camp verb passes, minus what each adds.
 *
 * The `attackFault`/`forageFault` pattern for the fourth time: a man who is
 * senseless, mid-swing or already busy is not laying a fire, and saying so once
 * means "may I" and "may I still" cannot drift apart.
 */
export function busyFault(world, catalogue, actor, tick, verb) {
    const combatant = world.combat[actor.id];
    if (!combatant)
        return null;
    if (isHelpless(catalogue, combatant, tick))
        return 'you cannot';
    if (isCommitted(combatant, tick))
        return 'you are in the middle of something';
    if (isChannelling(combatant, tick))
        return 'you are already busy';
    // What this verb asks of the body (M3-04). Checked here, inside the rule the
    // server enforces, rather than in the menu that displays it — a requirement
    // the UI applied and the handler did not would last exactly as long as it
    // took somebody to write their own client.
    if (verb)
        return capabilityFault(catalogue, combatant, verb);
    return null;
}
/* -------------------------------------------------------------------------- *
 * Building a fire
 * -------------------------------------------------------------------------- */
/** Why a fire cannot be laid here, or `null`. */
export function buildFault(world, catalogue, actor, tick) {
    const busy = busyFault(world, catalogue, actor, tick, 'build');
    if (busy !== null)
        return busy;
    if (!hasTag(world, catalogue, actor.id, FUEL_TAG))
        return 'you have nothing to burn';
    if (fireAt(world, actor.x, actor.y, tick) !== null)
        return 'there is a fire here already';
    const def = tileDefAt(world.map, actor.x, actor.y);
    if (!def.passable)
        return 'there is no room for a fire here';
    if (def.material === 'water')
        return 'you cannot light a fire on water';
    return null;
}
/**
 * How much harder wet ground makes a fire to start (RC1-01).
 *
 * A per-tile term on top of the weather, so that in the same downpour a fire is
 * likelier to take on a beaten floor or dry turf than on a peat bog or wet
 * shingle — which turns "find somewhere dry to lay it" into a real move rather
 * than a wish. Read off the tile *material*, like every other ground rule, so a
 * biome added later is judged by what it is made of and not by name.
 */
export const GROUND_KINDLE_PENALTY = 0.2;
export const SODDEN_MATERIALS = ['peat', 'sand'];
/**
 * The chance the tinder takes, here, now.
 *
 * Three terms, cheapest to reason about last: the **weather** (`kindleChance`,
 * minus its rain penalty when you are under a roof), the **ground** (a penalty
 * on materials that hold water), and the **flint**, which takes half of whatever
 * failure is left — the honest way to express "halves failure", since a flat
 * bonus would make a flint worthless in the dry and miraculous in a downpour.
 */
export function buildChance(world, catalogue, actorId) {
    const actor = world.actors[actorId];
    const sheltered = actor ? isSheltered(world.map, actor.x, actor.y) : false;
    // A roof protects the act and a prepared tinder bundle protects the material.
    // Together they turn firemaking from a rain-roll into a learnable recovery:
    // get inside, use what you kept dry, and the fire takes. Outside, or with
    // only damp loose fuel, the weather remains a real risk.
    if (sheltered && hasTag(world, catalogue, actorId, TINDER_TAG))
        return 1;
    let base = kindleChance(world.weather, { sheltered });
    if (actor && !sheltered) {
        const material = tileDefAt(world.map, actor.x, actor.y).material;
        if (SODDEN_MATERIALS.includes(material)) {
            base = Math.max(KINDLE_FLOOR, base - GROUND_KINDLE_PENALTY);
        }
    }
    if (!hasTag(world, catalogue, actorId, FLINT_TAG))
        return base;
    return 1 - (1 - base) * FLINT_FACTOR;
}
/**
 * A word on how well a fire would take here, or `null` when it would take fine
 * (RC1-01).
 *
 * The pre-commit half of firemaking legibility: the menu shows this beside "lay
 * a fire" so a player reads "soaked — it will likely not catch" *before* they
 * kneel for fifteen seconds and spend a bundle, rather than learning it from the
 * failure. Banded, not a percentage, so it reads like something a person would
 * say about the weather; `null` above the top band keeps the menu quiet when
 * conditions are good. Only meaningful where a fire could actually be laid — the
 * client shows it on the available verb, so it need not re-check the fault here.
 */
export const BUILD_EASY = 0.75;
export const BUILD_HARD = 0.45;
export function buildNote(world, catalogue, actorId) {
    const chance = buildChance(world, catalogue, actorId);
    if (chance >= BUILD_EASY)
        return null;
    if (chance >= BUILD_HARD)
        return 'damp — it may not take first time';
    return 'soaked — it will likely not catch';
}
export const buildHandler = {
    type: 'build',
    parse: parseBuild,
    check(intent, ctx) {
        const actor = ctx.world.actors[intent.actorId];
        if (!actor)
            return 'you are not in the world';
        return buildFault(ctx.world, ctx.catalogue, actor, ctx.tick);
    },
    apply(intent, ctx) {
        const actor = ctx.world.actors[intent.actorId];
        const combatant = actor ? ctx.world.combat[actor.id] : undefined;
        if (!actor || !combatant)
            return;
        // Channelled, unlike `kindle`: a brand is a moment and a fire is a job, and
        // fifteen seconds kneeling in the open is what makes building one somewhere
        // stupid a decision rather than a reflex.
        combatant.channel = {
            verb: 'build',
            targetId: null,
            startedAt: ctx.tick,
            completesAt: ctx.tick + BUILD_TICKS,
            fromX: actor.x,
            fromY: actor.y,
            detail: null,
        };
        ctx.emit({
            type: 'interacted',
            actors: [actor.id],
            refs: ['build', 'begin'],
            text: `${actor.name} kneels and starts laying a fire.`,
            audience: 'witnesses',
            at: { x: actor.x, y: actor.y },
        });
    },
};
/** Finish laying a fire. Called by `interactSystem`, which owns the clock. */
export function finishBuild(ctx, actor) {
    const fault = buildFault(ctx.world, ctx.catalogue, actor, ctx.tick);
    if (fault !== null) {
        ctx.emit({
            type: 'interacted',
            actors: [actor.id],
            refs: ['build', 'failed', fault],
            text: `${actor.name} gives it up.`,
            audience: 'witnesses',
            at: { x: actor.x, y: actor.y },
        });
        return;
    }
    const fuel = taggedItem(ctx.world, ctx.catalogue, actor.id, FUEL_TAG);
    if (!fuel)
        return;
    // Work out whether this particular lay catches before taking its fuel from
    // the satchel. A tinder bundle is itself fuel, so consulting the inventory
    // after removal would erase the very dry-tinder advantage that made this
    // sheltered fire dependable.
    const chance = buildChance(ctx.world, ctx.catalogue, actor.id);
    const inventory = ctx.world.inventories[actor.id];
    if (inventory)
        removeItem(inventory, fuel.id, 1);
    // Spent whether or not it catches, like `kindle`'s. Wood laid in the wet is
    // wood you no longer have.
    if (!ctx.rng('fire').chance(chance)) {
        // Name the cost, and — only if they are not already carrying one — teach the
        // flint (RC1-01). The bundle is gone either way, which is the thing worth
        // being told; the flint hint is how a wet-week player discovers the 40 grams
        // that would have doubled their odds, without a wiki to read it in.
        const hasFlint = hasTag(ctx.world, ctx.catalogue, actor.id, FLINT_TAG);
        ctx.emit({
            type: 'status',
            actors: [actor.id],
            refs: ['build', 'failed', ctx.world.weather.kind],
            text: hasFlint
                ? 'You coax the tinder and it will not take. The wood is spent.'
                : 'You coax the tinder and it will not take. The wood is spent — a flint and steel would give you a better chance of it.',
            audience: 'actor',
            to: actor.id,
        });
        return;
    }
    const id = `fire:${actor.id}:${ctx.tick}`;
    ctx.world.lights[id] = createLight(id, actor.x, actor.y, ctx.tick, {
        kind: 'fire',
        radius: FIRE_RADIUS,
        onTinder: isTinder(ctx.world, actor.x, actor.y),
    });
    ctx.emit({
        type: 'interacted',
        actors: [actor.id],
        refs: ['build', 'lit', id],
        text: isTinder(ctx.world, actor.x, actor.y)
            ? `${actor.name} gets a fire going. It is sitting in dry stuff that would rather it did not.`
            : `${actor.name} gets a fire going.`,
        audience: 'witnesses',
        at: { x: actor.x, y: actor.y },
    });
}
/* -------------------------------------------------------------------------- *
 * Feeding it
 * -------------------------------------------------------------------------- */
/**
 * Why this fire cannot be fed, or `null`.
 *
 * Lifted out of the handler by M3-04 so the affordance registry can ask the
 * same question the handler answers. Every verb's legality is a named function
 * for that reason, and this one and `restFault` were the last two that were not.
 */
export function feedFault(world, catalogue, actor, tick) {
    const busy = busyFault(world, catalogue, actor, tick, 'feed');
    if (busy !== null)
        return busy;
    if (!hasTag(world, catalogue, actor.id, FUEL_TAG))
        return 'you have nothing to burn';
    const fire = feedableFire(world, actor.x, actor.y, tick);
    if (!fire)
        return 'there is no fire here to feed';
    if (fire.outAt >= tick + FIRE_MAX_TICKS - FIRE_FEED_FLOOR) {
        return 'it is banked as high as it goes';
    }
    return null;
}
export const feedHandler = {
    type: 'feed',
    parse: parseFeed,
    check(intent, ctx) {
        const actor = ctx.world.actors[intent.actorId];
        if (!actor)
            return 'you are not in the world';
        return feedFault(ctx.world, ctx.catalogue, actor, ctx.tick);
    },
    // Instant, unlike building one: putting a log on is not a job, and a verb
    // that took fifteen seconds would mean nobody tended a fire through a fight.
    apply(intent, ctx) {
        const actor = ctx.world.actors[intent.actorId];
        if (!actor)
            return;
        const fire = feedableFire(ctx.world, actor.x, actor.y, ctx.tick);
        const fuel = taggedItem(ctx.world, ctx.catalogue, actor.id, FUEL_TAG);
        if (!fire || !fuel)
            return;
        if (!feedFire(fire, ctx.tick))
            return;
        const inventory = ctx.world.inventories[actor.id];
        if (inventory)
            removeItem(inventory, fuel.id, 1);
        ctx.emit({
            type: 'interacted',
            actors: [actor.id],
            refs: ['feed', fire.id],
            text: `${actor.name} puts more wood on, and the fire takes it.`,
            audience: 'witnesses',
            at: { x: actor.x, y: actor.y },
        });
    },
};
/* -------------------------------------------------------------------------- *
 * Butchery
 * -------------------------------------------------------------------------- */
/** How much of a carcass is left worth cutting off, in portions. */
export function portionsLeft(carcass, catalogue, tick) {
    const butchery = catalogue.butchery?.(carcass.speciesId);
    if (!butchery)
        return 0;
    // What the crows and the weather have had is gone: a fresh stag is worth all
    // of it, a turning one half, and a rotten one nothing at all.
    const stage = carcassStage(carcass, tick);
    const spoilage = stage === 'turning' ? 0.5 : stage === 'rotten' ? 0 : 1;
    const whole = Math.floor(butchery.portions * spoilage);
    return Math.max(0, whole - (carcass.taken ?? 0));
}
/**
 * A carcass within reach that still has meat on it, or `null`.
 *
 * First in id order rather than nearest, and deliberately: two bodies at your
 * feet is rare, and a tie broken by distance would have to break the remaining
 * tie somehow anyway. Sorting by id makes the choice a fact about the world
 * rather than about the insertion order of the carcass table.
 */
export function carcassInReach(world, catalogue, actor, tick) {
    let best = null;
    for (const id of Object.keys(world.carcasses).sort()) {
        const carcass = world.carcasses[id];
        if (!carcass)
            continue;
        if (chebyshev(actor.x, actor.y, carcass.x, carcass.y) > 1)
            continue;
        if (portionsLeft(carcass, catalogue, tick) <= 0)
            continue;
        if (best === null)
            best = carcass;
    }
    return best;
}
export function butcherFault(world, catalogue, actor, tick) {
    const busy = busyFault(world, catalogue, actor, tick, 'butcher');
    if (busy !== null)
        return busy;
    const carcass = carcassInReach(world, catalogue, actor, tick);
    if (!carcass)
        return 'there is nothing here worth cutting up';
    return null;
}
export const butcherHandler = {
    type: 'butcher',
    parse: parseButcher,
    check(intent, ctx) {
        const actor = ctx.world.actors[intent.actorId];
        if (!actor)
            return 'you are not in the world';
        return butcherFault(ctx.world, ctx.catalogue, actor, ctx.tick);
    },
    apply(intent, ctx) {
        const actor = ctx.world.actors[intent.actorId];
        const combatant = actor ? ctx.world.combat[actor.id] : undefined;
        if (!actor || !combatant)
            return;
        const carcass = carcassInReach(ctx.world, ctx.catalogue, actor, ctx.tick);
        if (!carcass)
            return;
        combatant.channel = {
            verb: 'butcher',
            targetId: null,
            startedAt: ctx.tick,
            completesAt: ctx.tick + BUTCHER_TICKS,
            fromX: actor.x,
            fromY: actor.y,
            detail: carcass.id,
        };
        ctx.emit({
            type: 'interacted',
            actors: [actor.id],
            refs: ['butcher', 'begin', carcass.speciesId],
            text: `${actor.name} sets to work on ${carcass.name}.`,
            audience: 'witnesses',
            at: { x: actor.x, y: actor.y },
        });
    },
};
/**
 * How the world dates a keepsake (M5-05).
 *
 * "The third week of the world" rather than a tick or a calendar date: the line
 * a trophy carries should read like something a bard would say, and a bard does
 * not say tick 540,000. Weeks are 1-based off world creation; the ordinals run
 * out well past anything an MVP playtest reaches, after which it falls back to a
 * plain number rather than inventing a word.
 */
export const WEEK_ORDINALS = [
    'first',
    'second',
    'third',
    'fourth',
    'fifth',
    'sixth',
    'seventh',
    'eighth',
    'ninth',
    'tenth',
    'eleventh',
    'twelfth',
];
export function weekWord(tick) {
    const week = Math.max(1, Math.ceil(gameTime(tick).day / 7));
    return WEEK_ORDINALS[week - 1] ?? `${week}th`;
}
/**
 * The line a trophy carries off the block (M5-05).
 *
 * Names what it came off and when. It does not claim who struck the felling
 * blow — the carcass does not record that, and a note that guessed would be the
 * one lie in a system whose whole point is that it never lies. What it says is
 * true: this came off this animal, by this hand, this week of the world.
 */
export function trophyProvenance(carcass, actor, tick) {
    return `Cut from ${carcass.name} by ${actor.name}, the ${weekWord(tick)} week of the world.`;
}
export function finishButcher(ctx, actor) {
    const fault = butcherFault(ctx.world, ctx.catalogue, actor, ctx.tick);
    if (fault !== null) {
        ctx.emit({
            type: 'interacted',
            actors: [actor.id],
            refs: ['butcher', 'failed', fault],
            text: `${actor.name} comes away with nothing.`,
            audience: 'witnesses',
            at: { x: actor.x, y: actor.y },
        });
        return;
    }
    const carcass = carcassInReach(ctx.world, ctx.catalogue, actor, ctx.tick);
    const butchery = ctx.catalogue.butchery?.(carcass.speciesId);
    const item = butchery ? ctx.catalogue.item?.(butchery.itemId) : null;
    if (!butchery || !item)
        return;
    // One trip takes what a person can carry off in one go. A stag is several
    // visits, which is what makes a big kill a thing you camp beside.
    const taken = Math.min(2, portionsLeft(carcass, ctx.catalogue, ctx.tick));
    const first = (carcass.taken ?? 0) === 0;
    carcass.taken = (carcass.taken ?? 0) + taken;
    // The first trip opens it up (M5-03). Field-dressing is what you do before
    // you cut anything off, so it is not a verb of its own — it is the thing the
    // first butchery *is*, and what it buys is a carcass that is still worth
    // hauling home tomorrow.
    carcass.dressedAt ??= ctx.tick;
    const inventory = (ctx.world.inventories[actor.id] ??= createInventory());
    addItem(inventory, item.id, taken, ctx.tick, item.stackable, undefined, item.shelfLife !== null);
    // The skin, the fleece, the tusks (M5-01). Off on the first trip only: you
    // skin an animal once, before you start cutting, and this is what the smith
    // actually wants — meat spoils on the walk home and a hide does not.
    const hides = first ? (butchery.yields ?? []) : [];
    for (const yielded of hides) {
        const def = ctx.catalogue.item?.(yielded.itemId);
        if (!def || yielded.count <= 0)
            continue;
        // A `trophy` yield remembers the hunt (M5-05): it comes off the block with
        // a provenance note, which — being a note — never merges into an anonymous
        // heap, so the pair of tusks in your bag is *this* boar's, taken by you,
        // this week of the world. Everything else is a trade good and stacks.
        const trophy = def.tags.includes('trophy');
        const note = trophy ? trophyProvenance(carcass, actor, ctx.tick) : undefined;
        addItem(inventory, def.id, yielded.count, ctx.tick, def.stackable, note);
        ctx.emit({
            type: 'interacted',
            actors: [actor.id],
            refs: ['butcher', 'yield', def.id, String(yielded.count)],
            text: `${actor.name} takes ${def.name} off ${carcass.name}.`,
            audience: 'witnesses',
            at: { x: actor.x, y: actor.y },
        });
    }
    ctx.emit({
        type: 'interacted',
        actors: [actor.id],
        refs: ['butcher', 'done', item.id, String(taken)],
        text: `${actor.name} cuts ${taken > 1 ? 'meat' : 'what meat there is'} off ${carcass.name}.`,
        audience: 'witnesses',
        at: { x: actor.x, y: actor.y },
    });
}
/* -------------------------------------------------------------------------- *
 * Cooking
 * -------------------------------------------------------------------------- */
/** What this raw thing becomes over a fire, or `null` if it is not raw. */
function cookedForm(catalogue, def) {
    const cookedId = def.tags.includes(RAW_TAG)
        ? def.id.replace(/^raw-/, 'cooked-')
        : def.tags.includes(ROASTABLE_TAG)
            ? `roasted-${def.id}`
            : null;
    if (!cookedId)
        return null;
    const cooked = catalogue.item?.(cookedId);
    return cooked?.tags.includes(COOKED_TAG) ? cooked : null;
}
function cookableItem(world, catalogue, actorId) {
    for (const tag of [RAW_TAG, ROASTABLE_TAG]) {
        const found = taggedItem(world, catalogue, actorId, tag);
        if (found && cookedForm(catalogue, found))
            return found;
    }
    return null;
}
export function cookFault(world, catalogue, actor, tick) {
    const busy = busyFault(world, catalogue, actor, tick, 'cook');
    if (busy !== null)
        return busy;
    if (fireAt(world, actor.x, actor.y, tick) === null)
        return 'there is no fire here';
    if (cookableItem(world, catalogue, actor.id))
        return null;
    // Nothing here a fire would turn into something else — but *why* matters to
    // the player (RC1-03). Standing at a fire with a satchel of hazelnuts and
    // being told "you have nothing worth cooking" reads as a bug: they plainly
    // have food. So the refusal distinguishes "you carry no food" from "the food
    // you carry is eaten as it is" — the honest reading of a game whose only
    // recipe, by design, is raw meat over a fire.
    if (hasTag(world, catalogue, actor.id, FOOD_TAG)) {
        return 'there is nothing here a fire would improve — it is eaten as it is';
    }
    return 'you have nothing worth cooking';
}
export const cookHandler = {
    type: 'cook',
    parse: parseCook,
    check(intent, ctx) {
        const actor = ctx.world.actors[intent.actorId];
        if (!actor)
            return 'you are not in the world';
        return cookFault(ctx.world, ctx.catalogue, actor, ctx.tick);
    },
    apply(intent, ctx) {
        const actor = ctx.world.actors[intent.actorId];
        const combatant = actor ? ctx.world.combat[actor.id] : undefined;
        if (!actor || !combatant)
            return;
        combatant.channel = {
            verb: 'cook',
            targetId: null,
            startedAt: ctx.tick,
            completesAt: ctx.tick + COOK_TICKS,
            fromX: actor.x,
            fromY: actor.y,
            detail: null,
        };
        ctx.emit({
            type: 'interacted',
            actors: [actor.id],
            refs: ['cook', 'begin'],
            text: `${actor.name} sets food over the fire.`,
            audience: 'witnesses',
            at: { x: actor.x, y: actor.y },
        });
    },
};
export function finishCook(ctx, actor) {
    const fault = cookFault(ctx.world, ctx.catalogue, actor, ctx.tick);
    if (fault !== null) {
        ctx.emit({
            type: 'interacted',
            actors: [actor.id],
            refs: ['cook', 'failed', fault],
            // The commonest cause is the fire having burned out under the pan, which
            // is a thing worth being told rather than left to guess at.
            text: `${actor.name} has nothing to cook it over.`,
            audience: 'witnesses',
            at: { x: actor.x, y: actor.y },
        });
        return;
    }
    const raw = cookableItem(ctx.world, ctx.catalogue, actor.id);
    const cooked = cookedForm(ctx.catalogue, raw);
    const inventory = ctx.world.inventories[actor.id];
    if (!inventory)
        return;
    // The whole lot at once. Cooking one portion at a time would be twelve
    // seconds of nothing repeated, which is a chore rather than a decision.
    const count = countOf(inventory, raw.id);
    removeItem(inventory, raw.id, count);
    addItem(inventory, cooked.id, count, ctx.tick, cooked.stackable, undefined, cooked.shelfLife !== null);
    ctx.emit({
        type: 'interacted',
        actors: [actor.id],
        refs: ['cook', 'done', cooked.id, String(count)],
        text: `${actor.name} takes ${cooked.name} off the fire.`,
        audience: 'witnesses',
        at: { x: actor.x, y: actor.y },
    });
}
/* -------------------------------------------------------------------------- *
 * Preparations (M4-03)
 * -------------------------------------------------------------------------- */
/**
 * The first preparation this satchel can satisfy, or `null`.
 *
 * In content's order, not by how good the result is: the table is the recipe
 * book, and "the first one I have the makings of" is what a person standing over
 * a fire with a handful of herbs actually does. Deterministic, which is the
 * requirement that rules out anything cleverer — a preparation chosen by
 * scanning the satchel in insertion order would replay differently after a
 * reload.
 *
 * ## Water, and what is standing in for it
 *
 * Both preparations want water, and M4-02 already decided the game has no vessel
 * to carry any in: water quality is read off the ground instead. The same
 * deferral applies here, one step further — the **fire** is what stands in for
 * boiling water, because a fire is a thing the world can already model and a pot
 * is not. When a vessel system lands, this is where the water goes.
 */
export function availablePreparation(world, catalogue, actorId) {
    const inventory = world.inventories[actorId];
    if (!inventory)
        return null;
    for (const preparation of catalogue.preparations?.() ?? []) {
        if (countOf(inventory, preparation.herbId) <= 0)
            continue;
        if (preparation.withId !== null && countOf(inventory, preparation.withId) <= 0)
            continue;
        if (!catalogue.item?.(preparation.id))
            continue;
        return preparation;
    }
    return null;
}
export function prepareFault(world, catalogue, actor, tick) {
    const busy = busyFault(world, catalogue, actor, tick, 'prepare');
    if (busy !== null)
        return busy;
    if (fireAt(world, actor.x, actor.y, tick) === null)
        return 'there is no fire here';
    if (!availablePreparation(world, catalogue, actor.id)) {
        return 'you have nothing here worth making up';
    }
    return null;
}
export const prepareHandler = {
    type: 'prepare',
    parse: parsePrepare,
    check(intent, ctx) {
        const actor = ctx.world.actors[intent.actorId];
        if (!actor)
            return 'you are not in the world';
        return prepareFault(ctx.world, ctx.catalogue, actor, ctx.tick);
    },
    apply(intent, ctx) {
        const actor = ctx.world.actors[intent.actorId];
        const combatant = actor ? ctx.world.combat[actor.id] : undefined;
        if (!actor || !combatant)
            return;
        combatant.channel = {
            verb: 'prepare',
            targetId: null,
            startedAt: ctx.tick,
            completesAt: ctx.tick + PREPARE_TICKS,
            fromX: actor.x,
            fromY: actor.y,
            detail: null,
        };
        ctx.emit({
            type: 'interacted',
            actors: [actor.id],
            refs: ['prepare', 'begin'],
            text: `${actor.name} sets to work over the fire with what they gathered.`,
            audience: 'witnesses',
            at: { x: actor.x, y: actor.y },
        });
    },
};
/**
 * Finish a preparation. Called by `interactSystem`, which owns the clock.
 *
 * The result is dated **now**, which is the whole cost of the mechanic: a
 * poultice made from moss picked five days ago is a fresh poultice with two days
 * in it, and the wilting herb that went into it is gone. Nothing here checks the
 * freshness of the ingredients, deliberately — a spent stack is worth nothing in
 * a wound (`isSpent`), and boiling it does not make it worth anything; what it
 * does mean is that a forager comes home and makes up their poultices rather
 * than letting the yarrow turn, which is exactly the rhythm the ticket wants.
 */
export function finishPrepare(ctx, actor) {
    const fault = prepareFault(ctx.world, ctx.catalogue, actor, ctx.tick);
    if (fault !== null) {
        ctx.emit({
            type: 'interacted',
            actors: [actor.id],
            refs: ['prepare', 'failed', fault],
            text: `${actor.name} gives it up.`,
            audience: 'witnesses',
            at: { x: actor.x, y: actor.y },
        });
        return;
    }
    const preparation = availablePreparation(ctx.world, ctx.catalogue, actor.id);
    const made = ctx.catalogue.item?.(preparation.id);
    const inventory = ctx.world.inventories[actor.id];
    if (!inventory)
        return;
    removeItem(inventory, preparation.herbId, 1);
    if (preparation.withId !== null)
        removeItem(inventory, preparation.withId, 1);
    addItem(inventory, made.id, 1, ctx.tick, made.stackable, undefined, made.shelfLife !== null);
    // Making a thing teaches you what it is. Not what it *does* — that is
    // `learnRemedy`'s, and it waits until you have watched it work on somebody.
    const lore = (ctx.world.lore[actor.id] ??= createLore());
    learn(lore, made.id);
    ctx.emit({
        type: 'interacted',
        actors: [actor.id],
        refs: ['prepare', 'done', made.id],
        text: `${actor.name} ${preparation.line}`,
        audience: 'witnesses',
        at: { x: actor.x, y: actor.y },
    });
}
/* -------------------------------------------------------------------------- *
 * Eating
 * -------------------------------------------------------------------------- */
export function eatFault(world, catalogue, actor, itemId, tick) {
    const busy = busyFault(world, catalogue, actor, tick, 'eat');
    if (busy !== null)
        return busy;
    const inventory = world.inventories[actor.id];
    if (!inventory || countOf(inventory, itemId) <= 0)
        return 'you are not carrying that';
    const def = catalogue.item?.(itemId);
    if (!def)
        return 'you are not carrying that';
    // A remedy is taken by the same verb as a meal (M4-03) — chewed bark, a brew
    // drunk hot — and refuses for different reasons, so it is judged first and
    // separately. A verb of its own was considered and dropped: `eat` already
    // names an item out of the satchel and already means "put this in your mouth",
    // and two keys for that would be two keys the player has to learn to tell
    // apart in the dark.
    const remedy = isRemedy(def);
    if (remedy) {
        if (!relieves(effectOf(catalogue, def.id)))
            return 'that would do nothing for you';
        return null;
    }
    if (!def.tags.includes(FOOD_TAG) || !def.nutrition)
        return 'that is not food';
    const needs = world.needs[actor.id];
    if (needs && needs.food >= MAX_FOOD)
        return 'you could not eat another thing';
    return null;
}
/**
 * Whether there is *anything* here worth eating, or why not.
 *
 * The menu's version of `eatFault`. `eat` names an item, but a menu cannot: it
 * has to answer "is this verb worth offering" before the player has chosen
 * what, and asking it about one arbitrary item would grey out `eat` for
 * somebody carrying a whole satchel of food and one stone.
 *
 * So it asks about the best case — is there at least one edible thing — which
 * is the honest reading of what the menu entry promises. The item is still
 * named on the way to the server, and `eatFault` still judges that one.
 */
export function anyMealFault(world, catalogue, actor, tick) {
    const busy = busyFault(world, catalogue, actor, tick, 'eat');
    if (busy !== null)
        return busy;
    const inventory = world.inventories[actor.id];
    // A remedy is worth offering the verb for even on a full belly (M4-03): a man
    // who has just eaten can still chew willow bark for his leg, and greying out
    // the only key that reaches it would hide the medicine behind the meal.
    const remedy = inventory?.items.some((stack) => {
        const def = catalogue.item?.(stack.defId);
        return (def !== null && def !== undefined && isRemedy(def) && relieves(effectOf(catalogue, def.id)));
    });
    if (remedy === true)
        return null;
    const needs = world.needs[actor.id];
    if (needs && needs.food >= MAX_FOOD)
        return 'you could not eat another thing';
    const edible = inventory?.items.some((stack) => {
        const def = catalogue.item?.(stack.defId);
        return def?.tags.includes(FOOD_TAG) === true && Boolean(def.nutrition);
    });
    return edible === true ? null : 'you have nothing to eat';
}
export const eatHandler = {
    type: 'eat',
    parse: parseEat,
    check(intent, ctx) {
        const actor = ctx.world.actors[intent.actorId];
        if (!actor)
            return 'you are not in the world';
        return eatFault(ctx.world, ctx.catalogue, actor, intent.payload.itemId, ctx.tick);
    },
    // Instant. Eating is the one camp verb that is not a job — and making it a
    // channel would mean a hungry man in a fight could not take a mouthful.
    apply(intent, ctx) {
        const actor = ctx.world.actors[intent.actorId];
        if (!actor)
            return;
        const def = ctx.catalogue.item?.(intent.payload.itemId);
        const inventory = ctx.world.inventories[actor.id];
        if (!def || !inventory)
            return;
        removeItem(inventory, def.id, 1);
        const needs = needsOf(ctx.world, actor.id);
        nourish(needs, def.nutrition ?? 0);
        const lore = ctx.world.lore[actor.id];
        const name = nameFor(def, lore);
        // The remedy half (M4-03). Taken, not eaten: it buys relief on a deadline
        // and it does nothing at all to a wound, which is the distinction the whole
        // herb is for.
        if (isRemedy(def)) {
            takeRemedy(ctx, actor, def, effectOf(ctx.catalogue, def.id), name);
            return;
        }
        // The gamble. Raw meat is food and it is a real meal; it is also a night
        // spent regretting it about a third of the time.
        if (def.tags.includes(RAW_TAG) && ctx.rng(`sickness:${actor.id}`).chance(RAW_SICKNESS_CHANCE)) {
            needs.illUntil = Math.max(needs.illUntil, ctx.tick + SICKNESS_TICKS);
            ctx.emit({
                type: 'status',
                actors: [actor.id],
                refs: ['sickened', def.id],
                text: `You eat ${name} raw. Within the hour you wish you had not.`,
                audience: 'actor',
                to: actor.id,
            });
            return;
        }
        ctx.emit({
            type: 'interacted',
            actors: [actor.id],
            refs: ['eat', def.id],
            text: `${actor.name} eats ${name}.`,
            audience: 'witnesses',
            at: { x: actor.x, y: actor.y },
        });
    },
};
/** Remove one exact row chosen in the satchel; stale or equipped rows fail safe. */
export const dropHandler = {
    type: 'drop',
    parse: parseDrop,
    check(intent, ctx) {
        const actor = ctx.world.actors[intent.actorId];
        if (!actor)
            return 'you are not in the world';
        const inventory = ctx.world.inventories[actor.id];
        if (!inventory)
            return 'there is nothing in your satchel';
        const index = stackIndexForKey(inventory, intent.payload.stackKey);
        if (index < 0)
            return 'that stack is no longer in your satchel';
        const stack = inventory.items[index];
        const loadout = ctx.world.combat[actor.id]?.loadout;
        if (stack && loadout?.weaponId === stack.defId)
            return 'you are holding that';
        if (stack && loadout?.armourIds.includes(stack.defId))
            return 'you are wearing that';
        return null;
    },
    apply(intent, ctx) {
        const actor = ctx.world.actors[intent.actorId];
        const inventory = ctx.world.inventories[intent.actorId];
        if (!actor || !inventory)
            return;
        const index = stackIndexForKey(inventory, intent.payload.stackKey);
        if (index < 0)
            return;
        const [stack] = inventory.items.splice(index, 1);
        if (!stack)
            return;
        const def = ctx.catalogue.item?.(stack.defId);
        const name = def ? nameFor(def, ctx.world.lore[actor.id]) : stack.defId;
        ctx.emit({
            type: 'interacted',
            actors: [actor.id],
            refs: ['drop', stack.defId, String(stack.count)],
            text: `You drop ${stack.count > 1 ? `${stack.count} × ` : ''}${name}.`,
            audience: 'actor',
            to: actor.id,
        });
    },
};
/** Swap a carried weapon into the character's hands, or put the held one away. */
export const equipHandler = {
    type: 'equip',
    parse: parseEquip,
    check(intent, ctx) {
        const actor = ctx.world.actors[intent.actorId];
        if (!actor)
            return 'you are not in the world';
        const combatant = ctx.world.combat[actor.id];
        if (!combatant)
            return 'you have no hands to put it in';
        if (isCommitted(combatant, ctx.tick) || isChannelling(combatant, ctx.tick)) {
            return 'your hands are busy';
        }
        if (combatant.engagedWith !== null || combatant.withdrawingUntil > ctx.tick) {
            return 'you cannot change weapons in an exchange';
        }
        if ('stow' in intent.payload) {
            const heldId = combatant.loadout.weaponId;
            const defaultId = ctx.catalogue.defaultLoadout?.weaponId;
            if (!heldId || heldId === defaultId)
                return 'your hands are already empty';
            if (!ctx.catalogue.weapon(heldId) || !ctx.catalogue.item?.(heldId)) {
                return 'you cannot put that in your satchel';
            }
            return null;
        }
        const inventory = ctx.world.inventories[actor.id];
        if (!inventory)
            return 'there is nothing in your satchel';
        const index = stackIndexForKey(inventory, intent.payload.stackKey);
        if (index < 0)
            return 'that stack is no longer in your satchel';
        const stack = inventory.items[index];
        if (!stack || !ctx.catalogue.weapon(stack.defId))
            return 'that is not a weapon';
        return null;
    },
    apply(intent, ctx) {
        const actor = ctx.world.actors[intent.actorId];
        const combatant = ctx.world.combat[intent.actorId];
        if (!actor || !combatant)
            return;
        if ('stow' in intent.payload) {
            const heldId = combatant.loadout.weaponId;
            const def = ctx.catalogue.item?.(heldId);
            const defaultId = ctx.catalogue.defaultLoadout?.weaponId;
            if (!def || !defaultId || heldId === defaultId)
                return;
            const inventory = (ctx.world.inventories[actor.id] ??= createInventory());
            if (countOf(inventory, heldId) === 0) {
                addItem(inventory, heldId, 1, ctx.tick, def.stackable);
            }
            combatant.loadout = { ...combatant.loadout, weaponId: defaultId };
            const name = nameFor(def, ctx.world.lore[actor.id]);
            ctx.emit({
                type: 'interacted',
                actors: [actor.id],
                refs: ['equip', 'stowed', heldId],
                text: `You put ${name} away.`,
                audience: 'actor',
                to: actor.id,
            });
            return;
        }
        const inventory = ctx.world.inventories[intent.actorId];
        if (!inventory)
            return;
        const index = stackIndexForKey(inventory, intent.payload.stackKey);
        const stack = index >= 0 ? inventory.items[index] : undefined;
        const weapon = stack ? ctx.catalogue.weapon(stack.defId) : null;
        if (!stack || !weapon)
            return;
        const puttingAway = combatant.loadout.weaponId === stack.defId;
        const weaponId = puttingAway ? (ctx.catalogue.defaultLoadout?.weaponId ?? '') : stack.defId;
        combatant.loadout = {
            ...combatant.loadout,
            weaponId,
            ...(!puttingAway && weapon.ranged && combatant.loadout.ammunition === undefined
                ? { ammunition: 0 }
                : {}),
            ...(!puttingAway && weapon.takesEdge && combatant.loadout.edge === undefined
                ? { edge: 'keen' }
                : {}),
        };
        const def = ctx.catalogue.item?.(stack.defId);
        const name = def ? nameFor(def, ctx.world.lore[actor.id]) : weapon.name;
        ctx.emit({
            type: 'interacted',
            actors: [actor.id],
            refs: ['equip', puttingAway ? 'stowed' : 'held', stack.defId],
            text: puttingAway ? `You put ${name} away.` : `You take ${name} in hand.`,
            audience: 'actor',
            to: actor.id,
        });
    },
};
/**
 * Take something for the pain (M4-03).
 *
 * Three things happen and the order of them is the design: the relief is written
 * onto the needs record with a deadline, the character *learns what the herb
 * does* — because they are about to feel it — and the log says so in a sentence
 * that describes an effect rather than quoting a number.
 *
 * What does not happen is anything at all to a wound. There is no line here that
 * touches `wound.pain`, `wound.state` or `bleedRate`, and that absence is the
 * acceptance criterion: a man on willow bark has exactly the wound he had, and
 * the body diagram will still say so.
 */
function takeRemedy(ctx, actor, def, effect, name) {
    const needs = needsOf(ctx.world, actor.id);
    const took = takeRelief(needs, ctx.tick, effect.pain ?? 0, effect.fever ?? 0, effect.relief ?? 0);
    if (!took) {
        ctx.emit({
            type: 'status',
            actors: [actor.id],
            refs: ['remedy', 'nothing', def.id],
            text: `You take ${name}, and feel no different for it.`,
            audience: 'actor',
            to: actor.id,
        });
        return;
    }
    const lore = (ctx.world.lore[actor.id] ??= createLore());
    learnRemedy(lore, def.id);
    ctx.emit({
        type: 'status',
        actors: [actor.id],
        refs: ['remedy', 'taken', def.id],
        // Their own business, like hunger: what somebody swallowed at their own fire
        // is not an event the valley witnessed.
        text: `You take ${name}. The ache goes quiet, and the wound is exactly where it was.`,
        audience: 'actor',
        to: actor.id,
    });
}
/* -------------------------------------------------------------------------- *
 * Rest
 * -------------------------------------------------------------------------- */
/**
 * Why they cannot lie down here, or `null`.
 *
 * Asks only about lying *down*. Getting up is always allowed, even mid-swing —
 * a rule that could trap somebody prone is a rule that gets people killed by
 * the interface — so the handler answers that half itself and never gets here.
 */
export function restFault(world, catalogue, actor, tick) {
    const busy = busyFault(world, catalogue, actor, tick, 'rest');
    if (busy !== null)
        return busy;
    if (!isPassable(world.map, actor.x, actor.y))
        return 'there is nowhere to lie down';
    const needs = world.needs[actor.id];
    if (needs && isResting(needs))
        return 'you are already lying down';
    return null;
}
export const restHandler = {
    type: 'rest',
    parse: parseRest,
    check(intent, ctx) {
        const actor = ctx.world.actors[intent.actorId];
        if (!actor)
            return 'you are not in the world';
        if (!intent.payload.on)
            return null;
        return restFault(ctx.world, ctx.catalogue, actor, ctx.tick);
    },
    apply(intent, ctx) {
        const actor = ctx.world.actors[intent.actorId];
        if (!actor)
            return;
        const needs = needsOf(ctx.world, actor.id);
        const combatant = ctx.world.combat[actor.id];
        if (!intent.payload.on) {
            if (!isResting(needs))
                return;
            wake(ctx, actor, needs, 'up');
            // `prone` is shared with knockdowns, but this particular prone state was
            // chosen by the player.  Getting up from a rest must clear it here rather
            // than rely on the combat recovery clock, which only handles knockdowns.
            if (combatant)
                combatant.vitals.prone = false;
            return;
        }
        needs.restingSince = ctx.tick;
        needs.restX = actor.x;
        needs.restY = actor.y;
        // Lying down *is* being prone: the same flag the fight rules already read,
        // so a sleeper defends like a man on his back without anybody writing a
        // second vulnerability rule. This is the risk the ticket wants a guard
        // rotation to emerge from, and it is real.
        if (combatant)
            combatant.vitals.prone = true;
        ctx.emit({
            type: 'interacted',
            actors: [actor.id],
            refs: ['rest', 'down', restQualityAt(ctx.world, actor.x, actor.y, ctx.tick, actor.id)],
            text: `${actor.name} lies down.`,
            audience: 'witnesses',
            at: { x: actor.x, y: actor.y },
        });
    },
};
/**
 * Get up — voluntarily, or because something woke you.
 *
 * A sleep long enough to be worth the name grants `well-rested`; a doze
 * interrupted after two minutes does not. The threshold is what stops the
 * bonus from being farmed by lying down and standing straight back up.
 */
export const WELL_RESTED_AFTER = TICKS_PER_GAME_HOUR * 4;
function wake(ctx, actor, needs, why) {
    const slept = ctx.tick - needs.restingSince;
    needs.restingSince = 0;
    const rested = slept >= WELL_RESTED_AFTER;
    if (rested)
        needs.restedUntil = ctx.tick + WELL_RESTED_TICKS;
    ctx.emit({
        type: 'interacted',
        actors: [actor.id],
        refs: ['rest', why, rested ? 'rested' : 'short'],
        text: why === 'woken'
            ? `${actor.name} is jolted awake.`
            : rested
                ? `${actor.name} gets up, and feels the better for it.`
                : `${actor.name} gets up.`,
        audience: why === 'woken' ? 'witnesses' : 'actor',
        ...(why === 'woken' ? { at: { x: actor.x, y: actor.y } } : { to: actor.id }),
    });
}
/* -------------------------------------------------------------------------- *
 * The systems
 * -------------------------------------------------------------------------- */
/**
 * Every tick: keep sleepers asleep, wake the ones something happened to, and
 * hand out what `well-rested` is worth.
 *
 * A `sim` system rather than an hourly one because two of its three jobs are
 * about things that happen in a second — you were moved, or you were hit — and
 * a game hour is two and a half real minutes of lying there having already been
 * stabbed.
 */
export const campSystem = {
    name: 'camp',
    phase: 'sim',
    run(ctx) {
        for (const id of Object.keys(ctx.world.needs).sort()) {
            const needs = ctx.world.needs[id];
            const actor = ctx.world.actors[id];
            if (!needs)
                continue;
            // Their body left the world. The appetite goes with it, or the table
            // grows without bound across a long-lived server.
            if (!actor) {
                delete ctx.world.needs[id];
                continue;
            }
            const combatant = ctx.world.combat[id];
            if (isResting(needs)) {
                if (actor.x !== needs.restX || actor.y !== needs.restY) {
                    // Dragged, or walked off in your sleep. Silent, like a cancelled
                    // channel: being moved is not an event the sleeper witnessed.
                    needs.restingSince = 0;
                    // A voluntary step is also getting up.  Without this, movement ended
                    // the rest record but left the shared combat posture behind forever,
                    // so the HUD continued to say "on the ground".
                    if (combatant)
                        combatant.vitals.prone = false;
                }
                else if (combatant && woundedWhileAsleep(combatant, needs)) {
                    wake(ctx, actor, needs, 'woken');
                }
                else if (combatant) {
                    // Still down. Re-asserted rather than set once, because standing up
                    // (M2-03) clears `prone` and a sleeper who stood up in their sleep
                    // would be a sleeper defending at full strength.
                    combatant.vitals.prone = true;
                }
            }
            // A bad meal, applied here rather than at the moment of eating: fever is
            // recomputed from the wound list whenever an infection changes (M1-05),
            // so a value written once would be wiped by the next roll. Re-asserting
            // it every tick costs a comparison and cannot be lost.
            if (isIll(needs, ctx.tick)) {
                if (combatant && combatant.vitals.fever < SICKNESS_FEVER) {
                    combatant.vitals.fever = SICKNESS_FEVER;
                }
            }
            else if (needs.illUntil !== 0) {
                needs.illUntil = 0;
                if (combatant)
                    recomputeFever(combatant.vitals);
            }
            // Willow bark, re-asserted onto the body for the reason the fever above is
            // (M4-03): the meter with the deadline on it lives on the needs record,
            // and the body carries only the consequence. Written every tick rather
            // than once, so a value cannot be lost to whatever else writes pain.
            if (isRelieved(needs, ctx.tick)) {
                if (combatant)
                    combatant.vitals.relief = needs.reliefPain;
            }
            else if (expireRelief(needs, ctx.tick)) {
                if (combatant)
                    combatant.vitals.relief = 0;
                ctx.emit({
                    type: 'status',
                    actors: [id],
                    refs: ['remedy', 'worn-off'],
                    text: 'Whatever you took for the pain has worn off.',
                    audience: 'actor',
                    to: id,
                });
            }
            if (combatant && isWellRested(needs, ctx.tick)) {
                topUp(ctx, combatant);
            }
        }
    },
};
/** Did anything cut them since they lay down? The thing that wakes a sleeper. */
function woundedWhileAsleep(combatant, needs) {
    for (const wound of combatant.vitals.wounds) {
        if (wound.inflictedAt >= needs.restingSince)
            return true;
    }
    return false;
}
/** The `well-rested` nudge: you get your wind back half again as fast. */
function topUp(ctx, combatant) {
    if (isCommitted(combatant, ctx.tick) || combatant.vitals.dead)
        return;
    const kit = kitOf(ctx.catalogue, combatant);
    if (!kit)
        return;
    const full = maxStamina(kit.plan, combatant.vitals, combatant.loadout.attributes) * STAMINA_SCALE;
    combatant.staminaMilli = Math.min(full, combatant.staminaMilli + Math.round(STAMINA_REGEN * WELL_RESTED_REGEN));
}
/**
 * On the hour: the belly empties, the bands bite, and sleepers heal.
 *
 * Hunger on the hour rather than the tick for the reason flora regrows on the
 * hour: it is a slow thing, and asking ten times a second whether somebody has
 * got hungry is ten times a second spent on a question whose answer changes
 * about twice a day.
 */
export const needsSystem = {
    name: 'needs',
    phase: 'hour',
    run(ctx) {
        for (const id of Object.keys(ctx.world.actors).sort()) {
            const actor = ctx.world.actors[id];
            const combatant = ctx.world.combat[id];
            if (!actor)
                continue;
            // Only people get hungry. A wolf that had to eat would need an appetite,
            // a food-seeking state and somewhere to find it, which is an ecology
            // rather than a needs meter — post-MVP, and honestly out of scope here.
            if (actor.kind !== 'player')
                continue;
            const needs = needsOf(ctx.world, id);
            const before = hungerBand(needs.food);
            // Cold-Hungered eats faster (M4-07). Read off the body through content,
            // so a world with no marks loaded starves everybody at exactly the old
            // rate.
            const marked = ctx.world.combat[id]?.vitals;
            starve(needs, TICKS_PER_GAME_HOUR, marked ? hungerRate(ctx.catalogue, marked) : 1);
            const band = hungerBand(needs.food);
            if (combatant) {
                combatant.vitals.hunger = band;
                combatant.vitals.nourishment = nourishmentFor(band);
            }
            if (band !== before) {
                ctx.emit({
                    type: 'status',
                    actors: [id],
                    refs: ['hunger', band, before],
                    text: band === 'starving' ? 'You are starving.' : 'You are getting hungry.',
                    // Their own business. Everyone in the valley announcing their
                    // appetite would drown a log that has a fight to report.
                    audience: 'actor',
                    to: id,
                });
            }
            if (combatant && isResting(needs)) {
                sleepRecovery(ctx, combatant, actor);
            }
        }
    },
};
/**
 * An hour of sleep, as recovery.
 *
 * A share of the daily rate rather than a second healing rule: `REST_SPEEDUP`
 * hours of it is worth a day of walking around in the same place, so eight
 * hours asleep beside a fire is a real night's healing and eight hours asleep
 * in a bog is still nothing at all.
 */
function sleepRecovery(ctx, combatant, actor) {
    const quality = restQualityAt(ctx.world, actor.x, actor.y, ctx.tick, actor.id);
    const rest = REST_RECOVERY[quality];
    if (rest <= 0)
        return;
    const rate = ((BLOOD_REGEN_PER_DAY * combatant.vitals.nourishment * (1 - combatant.vitals.fever) * rest) /
        24) *
        REST_SPEEDUP;
    if (rate <= 0)
        return;
    combatant.vitals.bloodMilli = Math.min(MAX_BLOOD_MILLI, combatant.vitals.bloodMilli + Math.round(rate * BLOOD_SCALE));
}
/**
 * On the day: blood comes back, and wounds knit (M1-04, M4-04).
 *
 * `advanceDailyRecovery` has been in `vitals.ts` since M1-04 and, like the
 * infection rolls M3-05 wired up, was only ever called by the offline harness.
 * This is the rule going live, with both of its inputs real: `nourishment` from
 * the belly, and `rest` from the ground the character is standing on.
 *
 * M4-04 adds the second half in the same place and off the same two inputs,
 * which is the reason it is here rather than in a system of its own: blood
 * coming back and a wound closing are one daily question about how somebody's
 * week is going, and asking it twice from two systems would be two places for
 * the rest tier to be read differently.
 */
export const recoverySystem = {
    name: 'recovery',
    phase: 'day',
    run(ctx) {
        for (const id of Object.keys(ctx.world.combat).sort()) {
            const combatant = ctx.world.combat[id];
            const actor = ctx.world.actors[id];
            if (!combatant || !actor)
                continue;
            const quality = restQualityAt(ctx.world, actor.x, actor.y, ctx.tick, actor.id);
            const rest = REST_RECOVERY[quality];
            for (const event of advanceDailyRecovery(combatant.vitals, rest)) {
                ctx.emit({
                    type: 'status',
                    actors: [id],
                    refs: [event.type, ...event.refs],
                    text: `${actor.name} is a little stronger.`,
                    audience: 'actor',
                    to: id,
                });
            }
            // Per-actor streams, like the infection rolls: one man's slow week must
            // not shift another man's dice.
            for (const event of advanceRecovery(ctx.rng(`recovery:${id}`), combatant.vitals, combatant.loadout.attributes, {
                rest,
                fed: combatant.vitals.nourishment,
                tick: ctx.tick,
                entryFor: woundEntryReader(ctx.catalogue),
            })) {
                ctx.emit({
                    type: 'status',
                    actors: [id],
                    refs: [event.type, ...event.refs],
                    text: event.type === 'healed'
                        ? 'It has closed over. There will be a scar.'
                        : 'The wound is knitting.',
                    // Their own body's business, like hunger and the infection roll: the
                    // valley does not need telling that somebody's arm is a little better.
                    audience: 'actor',
                    to: id,
                });
            }
        }
    },
};
/**
 * A reader for "what does this wound cost at level *n*", bound to a catalogue.
 *
 * The recovery rules take this as a callback rather than taking a catalogue,
 * which keeps `recovery.ts` on the same side of the content line as
 * `infection.ts` and `medicine.ts`: it is arithmetic over a body, and the tables
 * reach it the way every other piece of content reaches a rule.
 */
export function woundEntryReader(catalogue) {
    return (wound, level) => {
        const table = catalogue.woundTable;
        if (!table || !table.has(wound.type, wound.zone))
            return null;
        return table.resolve(wound.type, wound.zone, level);
    };
}
/* -------------------------------------------------------------------------- *
 * Fire that gets away from you
 * -------------------------------------------------------------------------- */
/**
 * Chance an unattended fire on dry tinder reaches the next tile, per game hour,
 * before the weather has its say.
 *
 * Modest on purpose. A fire built on heather in a dry spell should be a thing
 * that goes wrong over an hour or two while you are away foraging — long enough
 * to be your fault, short enough to be a story.
 */
export const SPREAD_CHANCE = 0.35;
/**
 * The most fires the valley will carry at once.
 *
 * A hard ceiling rather than a tuning knob: a wildfire is a drama and a burning
 * 256×256 commote is a tick budget. Combined with burnt ground that cannot burn
 * again, it means a fire that gets away always ends.
 */
export const MAX_FIRES = 24;
/**
 * On the hour: fires in dry stuff try to spread, and the ground they burned on
 * is left scorched.
 *
 * The self-limiting rule is the important half. A tile that has burned becomes
 * `dirt`, which is not tinder, so a fire cannot come back through where it has
 * already been — which turns "the moor is on fire" into a front that runs out
 * of moor rather than an unbounded one.
 */
export const fireSystem = {
    name: 'fires',
    phase: 'hour',
    run(ctx) {
        // Read the weather once: how well anything burns is a fact about the
        // valley this hour, not about each fire.
        const dryness = flammability(ctx.world.weather);
        if (dryness <= 0)
            return;
        for (const id of Object.keys(ctx.world.lights).sort()) {
            const light = ctx.world.lights[id];
            if (!light || light.kind !== 'fire' || !light.onTinder)
                continue;
            if (!isBurning(light, ctx.tick))
                continue;
            if (light.carriedBy !== null)
                continue;
            // Scorch its own tile first, so a fire that never spreads still leaves a
            // mark and still stops being able to.
            if (isTinder(ctx.world, light.x, light.y))
                setTile(ctx.world.map, light.x, light.y, 'dirt');
            if (Object.keys(ctx.world.lights).length >= MAX_FIRES)
                continue;
            if (!ctx.rng('fire').chance(SPREAD_CHANCE * dryness))
                continue;
            spread(ctx, light);
        }
    },
};
/** Put a fire on one adjacent tile that will take it. */
function spread(ctx, from) {
    for (const direction of DIRECTIONS) {
        const [dx, dy] = DIRECTION_VECTORS[direction];
        const x = from.x + dx;
        const y = from.y + dy;
        if (!isTinder(ctx.world, x, y))
            continue;
        if (fireAt(ctx.world, x, y, ctx.tick) === 'hearth')
            continue;
        if (hasFireOn(ctx.world.lights, x, y, ctx.tick))
            continue;
        const id = `fire:wild:${x},${y}:${ctx.tick}`;
        if (ctx.world.lights[id])
            continue;
        ctx.world.lights[id] = createLight(id, x, y, ctx.tick, {
            kind: 'fire',
            // Shorter than a laid fire: what is burning is the ground, and there is
            // not much of it. It is long enough to reach the next tile and no longer.
            burnTicks: TICKS_PER_GAME_HOUR * 2,
            onTinder: true,
        });
        ctx.emit({
            type: 'status',
            refs: ['fire', 'spread', String(x), String(y)],
            text: 'The fire gets into the ground and runs.',
            audience: 'witnesses',
            at: { x, y },
        });
        return;
    }
}
function hasFireOn(lights, x, y, tick) {
    for (const light of Object.values(lights)) {
        if (light.kind !== 'fire')
            continue;
        if (!isBurning(light, tick))
            continue;
        if (light.x === x && light.y === y)
            return true;
    }
    return false;
}
/* -------------------------------------------------------------------------- *
 * The rule set
 * -------------------------------------------------------------------------- */
/** Everything M3-06 installs on the rule set. */
export const CAMP_SYSTEMS = [
    campSystem,
    needsSystem,
    fireSystem,
    recoverySystem,
];
export const CAMP_INTENTS = [
    buildHandler,
    feedHandler,
    cookHandler,
    butcherHandler,
    eatHandler,
    equipHandler,
    dropHandler,
    restHandler,
    prepareHandler,
];
//# sourceMappingURL=camp.js.map